import React from 'react';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, Container, Grid, Typography } from '@mui/material';
// hooks
import useCallHistory, {
  CallHistoryFilterOption,
  callHistoryFilterOptions,
  CallLog,
} from '../../hooks/core-api/useCallHistory';
import useSettings from '../../hooks/useSettings';
//store
import useMainStore from 'src/store';
// components
import Page from 'src/components/Page';
import PageHeader from 'src/components/PageHeader';
import ReloadButton from 'src/components/ReloadButton';
import ReportTimeframeInput, {
  dateRangeToTimeframe,
  TimeframeDateRange,
  timeframes,
  timeframeToDateRange,
} from 'src/components/ReportTimeframeInput';
// sections
import CallHistoryFilter from 'src/sections/call-history/CallHistoryFilter';
import CallHistoryList from 'src/sections/call-history/CallHistoryList';
// images
import { PhoneConnectionIllustration } from 'src/assets';
// Sentry
import { captureException } from '@sentry/react';
//
import { DateRange } from 'src/types';

// Not currently set up to use dev 'json-server' server.
const CallHistoryPage = () => {
  const pageTitle = 'Call History';

  const defaultTimeframe = timeframes[0];
  const defaultDateRange = timeframeToDateRange(defaultTimeframe);

  const { themeStretch } = useSettings();
  const [dateRange, setDateRange] = React.useState<DateRange>(defaultDateRange);
  const [filters, setFilters] = React.useState<CallHistoryFilterOption[]>([]);

  const selectedLocations = useMainStore((state) => state.selectedLocations);

  const { fetchNextPage, hasNextPage, data, isFetching, isLoading, isError, error, refetch } =
    useCallHistory({
      request: {
        locationIds: selectedLocations?.map((x) => x.id),
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        callHistoryFilter: filters,
        limit: 50,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      analytics: {
        timeframe: dateRangeToTimeframe(dateRange).label,
      },
    });

  const firstPage = data?.pages?.[0];
  const firstPageHasData = firstPage && firstPage.items.length > 0;

  const handleTimeframeDateRangeChange = (tdr: TimeframeDateRange) => {
    console.log('Timeframe and Date range changed', tdr);
    setDateRange(tdr.dateRange);
  };

  if (isError) {
    captureException(error);
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    return (
      <>
        <Typography variant="h3">Error loading call history</Typography>
        {errorMessage && <Typography variant="subtitle1">{errorMessage}</Typography>}
      </>
    );
  }

  return (
    <Page title={pageTitle}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <PageHeader
              title={pageTitle}
              button={
                <ReloadButton
                  tooltip="Refresh report"
                  isFetching={isLoading || isFetching}
                  onRefetch={refetch}
                />
              }
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <ReportTimeframeInput
              onChange={handleTimeframeDateRangeChange}
              defaultTimeframe={defaultTimeframe}
            />
          </Grid>

          <Grid item xs={12} md={3} sx={{ mt: { sm: 1 } }}>
            <CallHistoryFilter
              onChange={(value) => setFilters(value)}
              options={callHistoryFilterOptions}
              value={filters}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            {isLoading && (
              <Typography variant="h5" sx={{ textAlign: 'center' }}>
                Loading...
              </Typography>
            )}
            {!firstPageHasData && !isLoading && (
              <Typography variant="h5" sx={{ textAlign: 'center' }}>
                No results to display
              </Typography>
            )}
            {data?.pages.map((group, i) => {
              if (group) {
                return (
                  <React.Fragment key={i}>
                    <Box sx={{ ml: 1.5 }}>
                      <CallHistoryList
                        callHistoryGroup={group.items}
                        previousCallHistoryGroup={data.pages[i - 1]?.items ?? ([] as CallLog[])}
                      />
                    </Box>
                  </React.Fragment>
                );
              }
              return null;
            })}
            {firstPageHasData && (
              <LoadingButton
                sx={{ ml: 0.5 }}
                onClick={() => fetchNextPage()}
                size="large"
                loading={isFetching}
                disabled={!hasNextPage}
              >
                More logs
              </LoadingButton>
            )}
          </Grid>
          <Grid item md={3} lg={2} xl={1} />
          <Grid
            item
            flexGrow={1}
            sx={{
              mt: 4,
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          >
            <Box sx={{ maxHeight: 300 }}>
              <PhoneConnectionIllustration />
            </Box>
          </Grid>
          <Grid item xs={1} />
        </Grid>
      </Container>
    </Page>
  );
};

export default CallHistoryPage;
